import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Edit2, Trash2, Calendar, Package, MessageSquare, Send, X, ShieldAlert } from 'lucide-react';
import { fetchOrderDetails, fetchOrderPayments, transformOrderDetails, transformOrderFromNeon, fetchBcProductImages, fetchUpsTracking, fetchUpsDeliveryPhoto, fetchVipRecord, fetchBcStoreCredit, fetchOrderNotes, fetchOrderHistory, fetchOrderBoxState, setBoxReview, saveOrderChanges, setOrderHold, cancelOrder, addOrderNote, bcProxy, fetchHomeValue } from '../services/api';
import { getCurrentUser } from '../services/auth';
import { toTitleCase } from '../utils/format';
import VipRewardsModal from '../components/VipRewardsModal';
import { PackingInvoiceModal, PackingVideoOverlay, fetchPackingStatus } from '../components/PackingCapture';
import { sessionHeaders } from '../services/auth';
import CardActions from '../components/CardActions';

const US_STATES = [['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming']];
const stateAbbr = (s) => { if (!s || s.length <= 2) return (s || '').toUpperCase(); const match = US_STATES.find(([, name]) => name.toLowerCase() === s.toLowerCase()); return match ? match[0] : s.toUpperCase(); };

// BigCommerce control-panel subdomain for crabplace.com (not a secret — it's
// the store admin host; BC login is still required). Used by the "View" link
// to deep-link an order in the BC admin. Source: GET /v2/store control_panel_base_url.
const BC_CONTROL_PANEL = 'https://store-3mvkyi71x6.mybigcommerce.com';

function fmtUpsDate(d, t) {
  // d = YYYYMMDD, t = HHMMSS
  const date = d && d.length === 8
    ? `${parseInt(d.slice(4,6),10)}/${parseInt(d.slice(6,8),10)}/${d.slice(2,4)}`
    : d || '';
  if (!t || t.length < 6) return date;
  const h = parseInt(t.slice(0,2), 10);
  const m = t.slice(2,4);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${date} ${h12}:${m} ${ampm}`;
}

function fmtHistDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtPhone(p) {
  if (!p) return '';
  const digits = p.replace(/\D/g, '');
  const d = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  return p;
}

function fmtNoteDate(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return v;
  const h = d.getHours() % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, '0');
  const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
  return `${d.getMonth()+1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)} ${h}:${min}${ampm}`;
}

const RC_PROXY = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions/ringcentral-proxy'
  : '/.netlify/functions/ringcentral-proxy';

async function sendSms(to, text) {
  const phone = to.replace(/\D/g, '').replace(/^(\d{10})$/, '+1$1');
  const res = await fetch(RC_PROXY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'sms-send', params: { to: phone, text } }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'SMS send failed');
  return data;
}

const PAGE_LABELS = {
  'dashboard': 'Dashboard',
  'customer-search': 'Customer Search',
  'shipping-today': 'Shipping Today',
  'remaining-today': 'Remaining Today',
  'placed-today': 'Placed Today',
  'on-hold': 'On Hold',
  'open-claims': 'Open Claims',
  'fraud-alert': 'Fraud Alert',
  'chargebacks': 'Chargebacks',
  'shipping-report': 'Shipping Report',
};

export default function OrderDetails({ orderId, navigateTo, fromPage }) {
  const [order, setOrder] = useState(null);
  const [hvLoading, setHvLoading] = useState({});
  const [error, setError] = useState(null);
  const [errorNotFound, setErrorNotFound] = useState(false);
  const [showOrderNotes, setShowOrderNotes] = useState(false);
  const [showSystemNotes, setShowSystemNotes] = useState(false);
  const [showProblems, setShowProblems] = useState(false);
  const [showAdditional, setShowAdditional] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [showTech, setShowTech] = useState(false);
  const [upsWeight, setUpsWeight] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shippingInstructions, setShippingInstructions] = useState('');
  const [packingNotes, setPackingNotes] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [upsData, setUpsData] = useState(null);
  const [upsLoading, setUpsLoading] = useState(false);
  const [upsError, setUpsError] = useState(null);
  const [upsOpen, setUpsOpen] = useState(false);
  const [vipColorStatus, setVipColorStatus] = useState('never');
  const [storeCredit, setStoreCredit] = useState(null);
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const [systemNotes, setSystemNotes] = useState([]);
  const [payments, setPayments] = useState(null);
  const [internalNotes, setInternalNotes] = useState([]);
  const [orderHistory, setOrderHistory] = useState(null);
  const upsRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [holdLoading, setHoldLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [boxReviewSaving, setBoxReviewSaving] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);
  const [showSms, setShowSms] = useState(false);
  const [smsBody, setSmsBody] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsResult, setSmsResult] = useState(null);
  const [addressPickerType, setAddressPickerType] = useState(null); // 'billing' | 'shipping' | null
  const [bcAddresses, setBcAddresses] = useState([]);
  const [bcAddressesLoading, setBcAddressesLoading] = useState(false);
  const [bcCustomerId, setBcCustomerId] = useState(null);
  const [newAddr, setNewAddr] = useState(null); // null = hidden, object = form data
  const [newAddrSaving, setNewAddrSaving] = useState(false);
  const [editingField, setEditingField] = useState(null); // e.g. 'billing.phone', 'shipping.email'
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [editingValue, setEditingValue] = useState('');
  const [attribution, setAttribution] = useState(null);
  const [socialNotes, setSocialNotes] = useState([]); // FB/IG comments attached to this customer
  const [showPackingInvoice, setShowPackingInvoice] = useState(false);
  const [showPackingVideo, setShowPackingVideo] = useState(false);
  const [packingStatus, setPackingStatus] = useState(null); // latest packing_recordings row

  useEffect(() => {
    if (!orderId) {
      setError('No order ID provided.');
      return;
    }
    setOrder(null);
    setError(null);
    setErrorNotFound(false);
    setVipColorStatus('never');
    setStoreCredit(null);
    setSystemNotes([]);
    setInternalNotes([]);
    setOrderHistory(null);
    setPackingStatus(null);
    fetchOrderDetails(orderId)
      .then(async raw => {
        const transformed = raw?._fromNeon
          ? transformOrderFromNeon(raw, null)
          : transformOrderDetails(raw, null);
        if (!transformed) throw new Error('Could not parse order response.');
        // Enrich products missing images with CURRENT BC catalog thumbnails.
        // Keyed on bcProductId (recovered from the legacy image URL server-side),
        // never catalogId -- that is the legacy id and matches the wrong BC product.
        const missing = transformed.products.filter(p => !p.image);
        if (missing.length > 0) {
          const bcImages = await fetchBcProductImages(missing).catch(() => ({}));
          transformed.products = transformed.products.map(p => {
            if (p.image) return p;
            const url = (p.bcProductId && bcImages[String(p.bcProductId)]) || bcImages[`name:${p.name}`];
            return url ? { ...p, image: url } : p;
          });
        }
        if (transformed.customer.username) {
          fetchVipRecord(transformed.customer.username).then(({ joinedYear, vipColorStatus: status, vipNotRenewing }) => {
            setVipColorStatus(status);
            setOrder(o => ({ ...o, customer: { ...o.customer, isVip: status === 'active', vipNotRenewing: !!vipNotRenewing, ...(joinedYear ? { vipJoinedDate: joinedYear } : {}) } }));
          });
          fetchBcStoreCredit(transformed.customer.username).then(({ credit, bcCustomerId: bcId }) => {
            setStoreCredit(credit);
            if (bcId) setBcCustomerId(bcId);
          });
        }
        // Fetch notes and history in parallel
        const internalId = transformed.internalId;
        const customerId = transformed.customer.id;
        if (!internalId) setPayments([]);
        if (internalId) {
          fetchOrderNotes(internalId, 2).then(notes => setSystemNotes(Array.isArray(notes) ? notes : [])).catch(() => {});
          fetchOrderPayments(internalId).then(rows => setPayments(Array.isArray(rows) ? rows : [])).catch(() => setPayments('error'));
          fetchOrderNotes(internalId, 1).then(notes => setInternalNotes(Array.isArray(notes) ? notes : [])).catch(() => {});
        }
        if (customerId) {
          fetchOrderHistory(customerId).then(h => setOrderHistory(h ?? null)).catch(() => {});
          // Social-media notes attached to this customer (FB/IG complaints etc.)
          fetch('/.netlify/functions/neon', {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...sessionHeaders() },
            body: JSON.stringify({ query: 'social_notes_for_customer', params: { customerId } }),
          }).then(r => r.json()).then(d => { if (Array.isArray(d.rows)) setSocialNotes(d.rows); }).catch(() => {});
        }
        // Fetch UTM attribution data
        const bcId = String(transformed.id);
        if (bcId) {
          fetch('/.netlify/functions/neon', {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...sessionHeaders() },
            body: JSON.stringify({ query: 'attribution_by_order', params: { bc_order_id: bcId } }),
          }).then(r => r.json()).then(d => {
            if (d.rows?.length) setAttribution(d.rows[0]);
          }).catch(() => {});
        }
        // Packing recording status (drives the "View Packing" pill)
        if (bcId) fetchPackingStatus(bcId).then(row => setPackingStatus(row)).catch(() => {});
        // A rep who placed this for the customer on crabplace.com (order-taken-by.js).
        // BigCommerce and the order sync both call these "Online".
        if (bcId) {
          fetch(`/.netlify/functions/order-taken-by?orderId=${encodeURIComponent(bcId)}`)
            .then(r => r.json())
            .then(d => { if (d.takenBy) setOrder(o => (o ? { ...o, takenBy: d.takenBy } : o)); })
            .catch(() => {});
        }
        setOrder(transformed);
        autoHomeValues(transformed);
        setTrackingNumber(transformed.trackingNumber);
        setShippingInstructions(transformed.shippingInstructions);
        setPackingNotes(transformed.packingNotes);
        setGiftMessage(transformed.giftMessage);
        if (raw?._fromNeon && internalId) {
          fetchOrderBoxState(internalId)
            .then(b => setOrder(o => (o && o.internalId === internalId ? { ...o, ...b } : o)))
            .catch(() => {});
        }
      })
      .catch(e => { setError(e.message); setErrorNotFound(!!e.notFound); });
  }, [orderId]);

  useEffect(() => {
    function handleClick(e) { if (upsRef.current && !upsRef.current.contains(e.target)) setUpsOpen(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // The scale weight is often blank, so pull the UPS weight for the Box Weight
  // readout without making anyone open the UPS Details panel.
  useEffect(() => {
    if (!order?.id || order.boxWeight > 0 || !order.trackingNumber || order.trackingNumber.length < 6) return;
    let live = true;
    setUpsWeight(null);
    fetchUpsTracking(order.trackingNumber)
      .then(d => { if (live) setUpsWeight(d?.charges?.billedWeight || d?.weight || null); })
      .catch(() => {});
    return () => { live = false; };
  }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpsClick = async () => {
    if (upsOpen) { setUpsOpen(false); return; }
    setUpsOpen(true);
    if (upsData || upsLoading) return;
    setUpsLoading(true);
    setUpsError(null);
    try {
      const data = await fetchUpsTracking(trackingNumber);
      // The pull API never returns the delivery-photo bytes; the actual image
      // arrives via the UPS Track Alert push and is stored in Neon. If we've
      // captured one for this tracking #, show it inline (prefer it over the
      // pull's empty photo field).
      if (!data.podImage) {
        try {
          const shot = await fetchUpsDeliveryPhoto(trackingNumber);
          if (shot && shot.found && shot.image) { data.podImage = shot.image; data.podPushed = true; }
        } catch (_) { /* non-fatal */ }
      }
      setUpsData(data);
    } catch(e) {
      setUpsError(e.message);
    } finally {
      setUpsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!order?.internalId) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      await saveOrderChanges(order.internalId, { trackingNumber, packingNotes, giftMessage, shippingInstructions });
      setSaveMsg({ ok: true, text: 'Saved.' });
    } catch (e) {
      setSaveMsg({ ok: false, text: e.message });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 4000);
    }
  };

  const handleHold = async () => {
    if (!order?.internalId) return;
    const isOnHold = order.status === 'On Hold';
    setHoldLoading(true);
    try {
      await setOrderHold(order.internalId, !isOnHold);
      setOrder(o => ({ ...o, status: isOnHold ? 'Processing' : 'On Hold' }));
    } catch (e) {
      alert(e.message);
    } finally {
      setHoldLoading(false);
    }
  };

  // Settle a multi-box order (same as the old admin's Order Details box panel).
  const handleBoxReview = async (count) => {
    if (boxReviewSaving || !order?.internalId) return;
    const n = count > 0 ? count : 1;
    const user = getCurrentUser();
    setBoxReviewSaving(true);
    try {
      await setBoxReview(order.internalId, n, user?.staffName);
      setOrder(o => ({ ...o, boxCount: n, needsBoxReview: false, boxReviewedAt: new Date().toISOString(), boxReviewedBy: user?.staffName || '' }));
    } catch (e) {
      alert('Could not record the box decision - the order still blocks printing. ' + e.message);
    } finally {
      setBoxReviewSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!order?.internalId) return;
    const isCancelled = order.status === 'Cancelled';
    const msg = isCancelled ? 'Uncancel this order?' : 'Cancel this order?';
    if (!window.confirm(msg)) return;
    setCancelLoading(true);
    const user = getCurrentUser();
    try {
      if (isCancelled) {
        // Uncancel — no dedicated endpoint, just flip status locally since API may vary
        setOrder(o => ({ ...o, status: 'Processing' }));
      } else {
        await cancelOrder(order.internalId, user?.id, user?.staffName);
        setOrder(o => ({ ...o, status: 'Cancelled' }));
      }
    } catch (e) {
      alert(e.message);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleAddNote = async (typeId) => {
    if (!order?.internalId || !newNote.trim()) return;
    setNoteLoading(true);
    const user = getCurrentUser();
    try {
      await addOrderNote(order.internalId, newNote.trim(), user?.staffName, typeId);
      const refreshed = await fetchOrderNotes(order.internalId, typeId);
      if (typeId === 1) setInternalNotes(Array.isArray(refreshed) ? refreshed : []);
      else setSystemNotes(Array.isArray(refreshed) ? refreshed : []);
      setNewNote('');
    } catch (e) {
      alert(e.message);
    } finally {
      setNoteLoading(false);
    }
  };

  const openAddressPicker = async (type) => {
    setAddressPickerType(type);
    setNewAddr(null);
    if (bcAddresses.length > 0) return; // already loaded
    const email = order?.customer?.username;
    if (!email) return;
    setBcAddressesLoading(true);
    try {
      const res = await bcProxy({ path: `/v3/customers?email:in=${encodeURIComponent(email)}&include=addresses`, method: 'GET' });
      const data = await res.json();
      const customer = data?.data?.[0];
      setBcAddresses(customer?.addresses || []);
      if (customer?.id) setBcCustomerId(customer.id);
    } catch { setBcAddresses([]); }
    setBcAddressesLoading(false);
  };

  const emptyAddr = { first_name: '', last_name: '', company: '', address1: '', address2: '', city: '', state_or_province: '', postal_code: '', country_code: 'US', phone: '', address_type: 'residential' };

  const deleteAddress = async (addr, e) => {
    e.stopPropagation();
    if (!addr.id) return;
    const label = [addr.first_name, addr.last_name].filter(Boolean).join(' ') + ' — ' + (addr.address1 || '');
    if (!window.confirm(`Delete this address?\n\n${label}`)) return;
    if (!window.confirm('Are you sure? This will permanently remove the address from BigCommerce.')) return;
    try {
      const res = await bcProxy({ path: `/v3/customers/addresses?id:in=${addr.id}`, method: 'DELETE' });
      if (res.ok) {
        setBcAddresses(prev => prev.filter(a => a.id !== addr.id));
      } else {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.title || 'Failed to delete');
      }
    } catch (err) {
      alert('Error deleting address: ' + err.message);
    }
  };

  const saveNewAddress = async () => {
    if (!bcCustomerId || !newAddr) return;
    if (!newAddr.first_name || !newAddr.address1 || !newAddr.city || !newAddr.state_or_province || !newAddr.postal_code) {
      alert('Please fill in name, address, city, state, and zip.');
      return;
    }
    setNewAddrSaving(true);
    try {
      const res = await bcProxy({
        path: '/v3/customers/addresses',
        method: 'POST',
        body: [{ ...newAddr, customer_id: Number(bcCustomerId) }],
      });
      const data = await res.json();
      console.log('[AddressPicker] BC response:', data);
      const created = data?.data?.[0];
      if (!created?.id) throw new Error(data?.title || data?.errors ? JSON.stringify(data.errors) : 'Failed to create address');
      // Refresh address list and auto-select
      setBcAddresses(prev => [...prev, created]);
      setNewAddr(null);
      selectAddress(created);
    } catch (e) {
      alert('Error creating address: ' + e.message);
    } finally {
      setNewAddrSaving(false);
    }
  };

  const selectAddress = (addr) => {
    const mapped = {
      name: [addr.first_name, addr.last_name].filter(Boolean).join(' '),
      address: [addr.address1 || addr.street_1, addr.address2 || addr.street_2].filter(Boolean).join(' '),
      city: addr.city || '',
      state: stateAbbr(addr.state_or_province || addr.state || ''),
      zip: addr.postal_code || addr.zip || '',
      phone: addr.phone || '',
      email: order.customer[addressPickerType]?.email || '',
      homeValue: order.customer[addressPickerType]?.homeValue || '',
    };
    if (addressPickerType === 'shipping') {
      mapped.residential = (addr.address_type || '').toLowerCase() === 'residential';
    }
    setOrder(o => ({
      ...o,
      customer: { ...o.customer, [addressPickerType]: { ...o.customer[addressPickerType], ...mapped } },
    }));
    setAddressPickerType(null);
  };

  // src is the order to look up for: the one on screen for a click, or the one that
  // just loaded for the automatic pull (state has not caught up with it yet).
  const lookupHomeValue = async (section, src = order) => {
    const cust = src && src.customer && src.customer[section];
    const customerId = src && src.customer && src.customer.id;
    if (!customerId || !cust || hvLoading[section]) return;
    setHvLoading(prev => ({ ...prev, [section]: true }));
    try {
      const r = await fetchHomeValue({ customerId, address: cust.address, city: cust.city, state: cust.state, zipCode: cust.zip, isBillingAddress: section === 'billing' });
      // Only write to the order it was looked up for; the user may have moved on.
      if (r && r.value != null) setOrder(o => (o && o.id === src.id ? { ...o, customer: { ...o.customer, [section]: { ...o.customer[section], homeValue: r.value } } } : o));
      return r;
    } catch (e) { /* leave the link as-is on failure */ }
    finally { setHvLoading(prev => ({ ...prev, [section]: false })); }
  };

  // Pull both home values in when an order opens, the way the other admin shows them.
  // A stored value comes back free; only a customer with none on file costs a RentCast
  // lookup, and an address RentCast has no value for is tried once per browser session.
  const autoHomeValues = (src) => {
    ['billing', 'shipping'].forEach(section => {
      const cust = src.customer && src.customer[section];
      if (!src.customer?.id || !cust || cust.homeValue || !cust.address) return;
      const key = `cpxHomeValueTried:${src.customer.id}:${section}`;
      try { if (sessionStorage.getItem(key)) return; } catch (e) { /* storage blocked: still look up */ }
      lookupHomeValue(section, src).then(r => {
        if (r && r.value == null) { try { sessionStorage.setItem(key, '1'); } catch (e) { /* ignore */ } }
      });
    });
  };

  // Inline edit for phone/email on billing/shipping
  const startEdit = (section, field) => {
    setEditingField(`${section}.${field}`);
    setEditingValue(order.customer[section]?.[field] || '');
  };
  const saveEdit = () => {
    if (!editingField) return;
    const [section, field] = editingField.split('.');
    setOrder(o => ({
      ...o,
      customer: { ...o.customer, [section]: { ...o.customer[section], [field]: editingValue } },
    }));
    setEditingField(null);
    setEditingValue('');
  };
  const cancelEdit = () => { setEditingField(null); setEditingValue(''); };

  const EditableField = ({ section, field, value, formatter }) => {
    const key = `${section}.${field}`;
    if (editingField === key) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <input
            type={field === 'email' ? 'email' : 'tel'}
            value={editingValue}
            onChange={e => setEditingValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
            autoFocus
            style={{ fontSize: 12, padding: '2px 6px', border: '1px solid var(--orange)', borderRadius: 4, outline: 'none', width: field === 'email' ? 180 : 130, fontFamily: 'Montserrat, sans-serif' }}
          />
          <span style={{ cursor: 'pointer', color: 'green', fontSize: 13, fontWeight: 700 }} onClick={saveEdit} title="Save">&#10003;</span>
          <span style={{ cursor: 'pointer', color: 'var(--gray-400)', fontSize: 13, fontWeight: 700 }} onClick={cancelEdit} title="Cancel">&#10005;</span>
        </span>
      );
    }
    return (
      <span style={{ cursor: 'pointer' }} onClick={() => startEdit(section, field)} title={`Click to edit ${field}`}>
        {formatter ? formatter(value) : value}
        <Edit2 size={10} style={{ marginLeft: 4, color: 'var(--orange)', opacity: 0.6, verticalAlign: 'middle' }} />
      </span>
    );
  };

  if (error || !orderId) {
    return (
      <div>
        <div className="breadcrumb">
          <a onClick={() => navigateTo(fromPage || 'dashboard')}>{PAGE_LABELS[fromPage] || 'Dashboard'}</a>
          <span>›</span>
          <span>Order Details</span>
        </div>
        <div className="page-header">Order Details</div>
        <div style={{ marginTop: 24, maxWidth: 400 }}>
          {error && orderId && errorNotFound && (
            <div style={{ marginBottom: 16, padding: 14, fontSize: 13, color: '#8a5a00', background: '#fff8ec', border: '1px solid #f5d98a', borderRadius: 6 }}>
              <strong>Order #{orderId} not found.</strong> Double-check the order number and try again.
            </div>
          )}
          {error && orderId && !errorNotFound && (
            <div style={{ marginBottom: 16, padding: 14, fontSize: 13, color: '#c0392b', background: '#fff5f5', border: '1px solid #fcc', borderRadius: 6 }}>
              <strong>Error loading order {orderId}:</strong> {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              className="form-input"
              placeholder="Enter Order ID"
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  navigateTo('order-details', e.target.value.trim());
                }
              }}
              style={{ flex: 1, fontSize: 13 }}
            />
            <button className="btn btn-primary" onClick={e => {
              const input = e.currentTarget.previousSibling;
              if (input.value.trim()) navigateTo('order-details', input.value.trim());
            }}>Go</button>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div>
        <div className="breadcrumb">
          <a onClick={() => navigateTo(fromPage || 'dashboard')}>{PAGE_LABELS[fromPage] || 'Dashboard'}</a>
          <span>›</span>
          <span>Order Details</span>
        </div>
        <div className="page-header">Order Details</div>
        <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Loading order...</div>
      </div>
    );
  }

  const vipBg        = vipColorStatus === 'active' ? '#fdf3d0' : vipColorStatus === 'expired' ? '#e6f4ea' : undefined;
  const vipStrip     = vipColorStatus === 'active' ? '#b8860b' : vipColorStatus === 'expired' ? '#1e8e3e' : undefined;
  const vipHeaderBg    = vipColorStatus === 'active' ? '#f8eabc' : vipColorStatus === 'expired' ? '#ccebd6' : undefined;
  const vipHeaderColor = vipHeaderBg ? '#1a1400' : undefined;
  // Customer Analysis link: the login email covers every account under it; guests
  // (customer id 0) fall back to the order's own customer number.
  const analysisKey = order.customer.username
    || (order.customer.id && order.customer.id !== '0' ? order.customer.id : '');

  return (
    <div>
      <div className="breadcrumb">
        <a onClick={() => navigateTo(fromPage || 'dashboard')}>{PAGE_LABELS[fromPage] || 'Dashboard'}</a>
        <span>›</span>
        <span>Order Details</span>
      </div>
      <div className="page-header" style={vipBg ? { background: vipBg, borderRadius: 6, padding: '6px 10px', marginBottom: 8 } : undefined}>Order Details</div>
      {/* The new order screen CS is trying on real orders (OrderDesk.js). */}
      <a className="text-orange" style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600 }} onClick={() => navigateTo('order-desk', order.id)}>Try the new Order Desk (beta) ›</a>
      <div style={{ marginBottom: 8 }} />

      <div className="card od-customer-card" style={vipStrip ? { borderLeft: `4px solid ${vipStrip}` } : undefined}>
        <div className="vip-badge-large od-customer-ident" style={{ cursor: 'pointer' }}
          onClick={() => navigateTo('customer-search', null, { email: order.customer.username, autoSelect: true })}>
          <div>
            <div className="customer-num">Customer #{order.customer.id}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div className="customer-name">{toTitleCase(order.customer.billing.name)}</div>
              {order.customer.isVip && <span style={{ fontSize: 16, lineHeight: 1, marginBottom: 2 }}>👑</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              {order.customer.isVip ? (
                <>
                  <div style={{ fontSize: 10, background: 'var(--orange)', color: 'white', display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontWeight: 700, letterSpacing: '0.5px', whiteSpace: 'nowrap' }}
                    title={order.customer.vipNotRenewing ? 'Active VIP Club member who has opted OUT of auto-renewal' : 'Active VIP Club member'}>
                    {order.customer.vipNotRenewing ? 'VIP MEMBER - NOT RENEWING' : 'VIP MEMBER'}
                  </div>
                  {order.customer.vipJoinedDate && <div style={{ fontSize: 9, color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' }}>Member since {order.customer.vipJoinedDate}</div>}
                </>
              ) : vipColorStatus === 'expired' ? (
                <div style={{ fontSize: 9, color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' }}>Expired VIP Member</div>
              ) : vipColorStatus === 'never' ? (
                <div style={{ fontSize: 9, color: 'var(--gray-400)', fontWeight: 700, whiteSpace: 'nowrap' }}>Not a VIP Member</div>
              ) : null}
            </div>
            {analysisKey && (
              <a href={`/?page=customer-analysis&q=${encodeURIComponent(analysisKey)}`}
                target="_blank" rel="noopener noreferrer"
                title="This customer's whole history: spend by year, what they buy, problems on file, every order"
                style={{ display: 'inline-block', marginTop: 8, fontSize: 11, fontWeight: 700, color: '#fff', textDecoration: 'underline', textUnderlineOffset: 3 }}>
                View customer analysis report ↗
              </a>
            )}
          </div>
        </div>
        <div className="od-customer-cols">
        <div>
          <div className="customer-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Billing Info
            <Edit2 size={14} style={{ cursor: 'pointer', color: 'var(--orange)', padding: 2, borderRadius: 3, transition: 'background 0.15s' }} onClick={() => openAddressPicker('billing')} title="Edit billing address" onMouseEnter={e => e.currentTarget.style.background = 'var(--orange-100, rgba(255,140,0,0.1))'} onMouseLeave={e => e.currentTarget.style.background = 'none'} />
          </div>
          <div className="address-block">
            {toTitleCase(order.customer.billing.name)}<br />
            {toTitleCase(order.customer.billing.address)}<br />
            {toTitleCase(order.customer.billing.city)}, {order.customer.billing.state} {order.customer.billing.zip}<br />
            <EditableField section="billing" field="phone" value={order.customer.billing.phone} formatter={fmtPhone} /><br />
            <EditableField section="billing" field="email" value={order.customer.billing.email} />
          </div>
          <a className="address-link">📍 Map This Address</a>
          <a className="address-link" style={{ cursor: 'pointer' }} title="Get/update home value (RentCast)" onClick={() => lookupHomeValue('billing')}>🏠 Home Value: {order.customer.billing.homeValue ? '$' + Number(order.customer.billing.homeValue).toLocaleString('en-US', { maximumFractionDigits: 0 }) : (hvLoading.billing ? 'Looking up...' : 'Get Home Value')}</a>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <a className="text-orange" style={{ cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Send Email Confirmation</a>
            <a className="text-orange" style={{ cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Resend Email Confirmation</a>
            <a className="text-orange" style={{ cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Send UPDATED Email Confirmation</a>
          </div>
        </div>
        <div>
          <div className="customer-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Shipping Info
            <Edit2 size={14} style={{ cursor: 'pointer', color: 'var(--orange)', padding: 2, borderRadius: 3, transition: 'background 0.15s' }} onClick={() => openAddressPicker('shipping')} title="Edit shipping address" onMouseEnter={e => e.currentTarget.style.background = 'var(--orange-100, rgba(255,140,0,0.1))'} onMouseLeave={e => e.currentTarget.style.background = 'none'} />
          </div>
          <div className="address-block">
            {toTitleCase(order.customer.shipping.name)}<br />
            {toTitleCase(order.customer.shipping.address)}<br />
            {toTitleCase(order.customer.shipping.city)}, {order.customer.shipping.state} {order.customer.shipping.zip}<br />
            <EditableField section="shipping" field="phone" value={order.customer.shipping.phone} formatter={fmtPhone} /><br />
            <EditableField section="shipping" field="email" value={order.customer.shipping.email} />
          </div>
          {order.customer.shipping.residential && (
            <div style={{ marginTop: 4, fontSize: 11, color: 'var(--gray-500)', fontStyle: 'italic' }}>** Residential **</div>
          )}
          <a className="address-link">📍 Map This Address</a>
          <a className="address-link" style={{ cursor: 'pointer' }} title="Get/update home value (RentCast)" onClick={() => lookupHomeValue('shipping')}>🏠 Home Value: {order.customer.shipping.homeValue ? '$' + Number(order.customer.shipping.homeValue).toLocaleString('en-US', { maximumFractionDigits: 0 }) : (hvLoading.shipping ? 'Looking up...' : 'Get Home Value')}</a>
        </div>
          <div>
            <div className="customer-section-title">Account</div>
          <div style={{ fontSize: 12, marginBottom: 4 }}><strong>Login:</strong> {order.customer.username}</div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}><strong>Password:</strong> {order.customer.password}</div>
          <div
            style={{ marginTop: 8, fontSize: 12, cursor: bcCustomerId ? 'pointer' : 'default' }}
            onClick={() => { if (bcCustomerId) setRewardsOpen(true); }}
            title={bcCustomerId ? 'Click to view reward history' : ''}
          >
            VIP Rewards Cash: <strong style={{ color: 'var(--green)', textDecoration: bcCustomerId ? 'underline dotted' : 'none', textUnderlineOffset: 3 }}>{storeCredit !== null ? `$${Number(storeCredit).toFixed(2)}` : '—'}</strong>
          </div>
            <div style={{ marginTop: 10 }} />
          <div style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--gray-500)', fontWeight: 600, marginBottom: 2 }}>Email Status</div>
              <span className={`email-status ${order.customer.emailStatus}`}>{order.customer.emailStatus}</span>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--gray-500)', fontWeight: 600, marginBottom: 2 }}>SMS Status</div>
              <span className={`email-status ${order.customer.smsStatus}`}>{order.customer.smsStatus}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <a className="text-orange" style={{ cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>(Click to Email, Unsubscribe)</a>
            <a className="text-orange" style={{ cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>(Click to SMS, Unsubscribe)</a>
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>Text "Join" to 91597</div>
          </div>
          <div>
            <div className="customer-section-title">Customer History</div>
            <div className="history-stat"><label>Total Orders</label><span>{orderHistory?.summary?.totalNumberOfOrders ?? 0}</span></div>
            <div className="history-stat"><label>First Order</label><span>{fmtHistDate(orderHistory?.summary?.firstOrderDate)}</span></div>
            <div className="history-stat"><label>Last Order</label><span>{fmtHistDate(orderHistory?.summary?.lastOrderDate)}</span></div>
            <div className="history-stat"><label>Avg Profit Per Order</label><span>{orderHistory?.summary ? `$${Number(orderHistory.summary.averageProfitPerOrder).toFixed(2)}` : '—'}</span></div>
            <div className="history-stat"><label>Avg Order</label><span>{orderHistory?.summary ? `$${Number(orderHistory.summary.averageOrderAmount).toFixed(2)}` : '—'}</span></div>
          </div>
        </div>
      </div>

      <div className="order-details-grid" style={{ background: vipBg }}>
        {/* LEFT PANEL */}
        <div style={vipStrip ? { borderLeft: `4px solid ${vipStrip}`, background: vipBg, borderRadius: '0 0 0 6px' } : undefined}>
          <div className="card">
            <div style={{ padding: '16px 16px 0' }}>
              <span className={`order-status-badge status-${order.status.toLowerCase()}`}>{order.status}</span>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)', marginBottom: 16 }}>Order #{order.id}</h2>

              <div className="order-actions" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <button
                  className="btn btn-outline"
                  disabled={cancelLoading}
                  onClick={handleCancel}
                  style={{ color: order.status === 'Cancelled' ? 'var(--green)' : 'var(--red)' }}
                >
                  {cancelLoading ? '...' : order.status === 'Cancelled' ? 'Uncancel Order' : 'Cancel Order'}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={holdLoading}
                  onClick={handleHold}
                  style={order.status === 'On Hold' ? { background: 'var(--gray-500)' } : undefined}
                >
                  {holdLoading ? '...' : order.status === 'On Hold' ? 'Release Hold' : 'Place On Hold'}
                </button>
                <button
                  className="btn btn-outline"
                  style={{ color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 4 }}
                  onClick={() => {
                    const user = getCurrentUser();
                    const firstName = (order.customer.billing.name || '').split(' ')[0];
                    setSmsBody(`Hi ${toTitleCase(firstName)}, it's ${user?.staffName || 'The Crab Place'} with CrabPlace.com and I'm contacting you about your order.`);
                    setSmsResult(null);
                    setShowSms(true);
                  }}
                >
                  <MessageSquare size={13} /> Text Customer
                </button>
                {(() => {
                  // Show File Claim button if: order is completed/shipped AND (late delivery OR has damage/loss flag)
                  const parseDate = (s) => {
                    if (!s) return null;
                    // Handle M/D/YY with optional time: "3/5/26", "3/5/26 6:00AM", "3/6/26 12:41PM"
                    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
                    if (m) {
                      let yr = parseInt(m[3], 10);
                      if (yr < 100) yr += 2000;
                      // Extract time if present, normalize "6:00AM" → "6:00 AM"
                      let timeStr = s.slice(m[0].length).trim();
                      timeStr = timeStr.replace(/(\d)(AM|PM)/i, '$1 $2');
                      if (timeStr) {
                        const d = new Date(`${m[1]}/${m[2]}/${yr} ${timeStr}`);
                        if (!isNaN(d)) return d;
                      }
                      return new Date(yr, parseInt(m[1],10) - 1, parseInt(m[2],10));
                    }
                    const d = new Date(s);
                    return isNaN(d) ? null : d;
                  };
                  const selected = parseDate(order.selectedDelivery);
                  const actual = parseDate(order.actualDelivery);
                  const isLate = selected && actual && actual > selected;
                  const isDelivered = order.status === 'Completed' || order.status === 'Shipped' || actual;
                  const hasTracking = order.trackingNumber && order.trackingNumber.length > 5;
                  if (isDelivered && hasTracking) {
                    return (
                      <button
                        className="btn btn-outline"
                        style={{ color: isLate ? 'var(--orange)' : 'var(--navy)', display: 'flex', alignItems: 'center', gap: 4, borderColor: isLate ? 'var(--orange)' : undefined }}
                        onClick={() => setShowClaimModal(true)}
                      >
                        <ShieldAlert size={13} />
                        {isLate ? 'File Claim (Late)' : 'File Claim'}
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* SMS Compose Panel */}
              {showSms && (
                <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <MessageSquare size={13} /> Text Customer — {order.customer.billing.phone || order.customer.shipping.phone}
                    </div>
                    <button onClick={() => setShowSms(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)' }}><X size={14} /></button>
                  </div>
                  <textarea
                    value={smsBody}
                    onChange={e => setSmsBody(e.target.value)}
                    rows={3}
                    className="form-input"
                    style={{ width: '100%', fontSize: 12, resize: 'vertical', marginBottom: 8 }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      className="btn btn-primary"
                      disabled={smsSending || !smsBody.trim()}
                      style={{ fontSize: 11, padding: '6px 16px', display: 'flex', alignItems: 'center', gap: 4 }}
                      onClick={async () => {
                        const phone = order.customer.billing.phone || order.customer.shipping.phone;
                        if (!phone) { setSmsResult({ ok: false, msg: 'No phone number on this order' }); return; }
                        setSmsSending(true); setSmsResult(null);
                        try {
                          await sendSms(phone, smsBody);
                          setSmsResult({ ok: true, msg: 'SMS sent!' });
                          setTimeout(() => setShowSms(false), 2000);
                        } catch (err) { setSmsResult({ ok: false, msg: err.message }); }
                        finally { setSmsSending(false); }
                      }}
                    >
                      <Send size={12} /> {smsSending ? 'Sending...' : 'Send SMS'}
                    </button>
                    {smsResult && <span style={{ fontSize: 11, fontWeight: 600, color: smsResult.ok ? '#1a8a4a' : '#c0392b' }}>{smsResult.msg}</span>}
                  </div>
                </div>
              )}

              <div className="order-meta">
                <div className="order-meta-item">
                  <label>Order Date/Time</label>
                  <span>{order.orderDate}</span>
                </div>
                <div className="order-meta-item">
                  <label>Scale Delivery Date</label>
                  <span>{order.scaleDelivery || '—'}</span>
                </div>
                <div className="order-meta-item">
                  <label>Ship Date</label>
                  <span>{order.shipDate}</span>
                </div>
                <div className="order-meta-item">
                  <label>Estimated Delivery Date</label>
                  <span>{order.estimatedDelivery || '—'}</span>
                </div>
                <div className="order-meta-item">
                  <label>Selected Delivery Date</label>
                  <a style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {order.selectedDelivery} <Calendar size={13} />
                  </a>
                </div>
                <div className="order-meta-item">
                  <label>Actual Delivery Date</label>
                  <span>{order.actualDelivery || '—'}</span>
                </div>
              </div>

              <div className="order-form-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="form-label">Shipping Instructions</label>
                  <textarea className="form-textarea" value={shippingInstructions} onChange={e => setShippingInstructions(e.target.value)} style={{ minHeight: 60 }} />
                </div>
                <div>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 12 }}>Fraud Score: <strong style={{ color: order.fraudScore > 0 ? 'var(--red)' : 'var(--green)' }}>{order.fraudScore}</strong></span>
                    <span style={{ marginLeft: 10 }}><a className="text-orange" style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Add to Fraud Address</a></span>
                  </div>
                  {order.boxCount != null && (
                    <div style={{ marginBottom: 8, fontSize: 12 }}>
                      Boxes: <strong>{order.boxCount}</strong>
                      {order.boxReviewedAt && <span style={{ color: 'var(--gray-500)' }}> (settled by {order.boxReviewedBy || 'staff'})</span>}
                    </div>
                  )}
                  {order.needsBoxReview && (
                    <div style={{ border: '1px solid var(--red)', borderRadius: 4, padding: 8, marginBottom: 8 }}>
                      <div style={{ color: 'var(--red)', fontWeight: 700, fontSize: 13 }}>&#9888; This order needs {order.boxCount} boxes</div>
                      <div style={{ fontSize: 12, color: 'var(--gray-500)', margin: '4px 0 8px' }}>
                        Packing slips and invoices are blocked until someone settles this. Confirm the count, or set it back to 1 if it really fits in one box.
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-primary" disabled={boxReviewSaving} onClick={() => handleBoxReview(order.boxCount)}>
                          {boxReviewSaving ? '...' : `Confirm ${order.boxCount} boxes`}
                        </button>
                        <button className="btn btn-outline" disabled={boxReviewSaving} onClick={() => handleBoxReview(1)}>
                          It fits in 1 box
                        </button>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="form-label">Tracking Number</label>
                    <input className="form-input" value={trackingNumber} onChange={e => { setTrackingNumber(e.target.value); setUpsData(null); }} />
                  </div>
                  <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center' }}>
                    <a className="text-orange" style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Update BigCommerce</a>
                    <a
                      className="text-orange"
                      style={{ cursor: order?.id ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600, opacity: order?.id ? 1 : 0.5 }}
                      title={order?.id ? `Open order #${order.id} in the BigCommerce admin` : 'No BigCommerce order id'}
                      onClick={() => order?.id && window.open(`${BC_CONTROL_PANEL}/manage/orders/${order.id}`, '_blank', 'noopener')}
                    >View in BigCommerce</a>
                    {trackingNumber && (
                      <div ref={upsRef} style={{ position: 'relative' }}>
                        <a onClick={handleUpsClick} style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--orange)', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Package size={12} /> UPS Details
                        </a>
                        {upsOpen && (
                          <div className="ups-panel" style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, width: 320, background: 'white', border: '1px solid var(--gray-200)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 1000, padding: 14, fontSize: 12 }}>
                            {upsLoading && <div style={{ color: 'var(--gray-500)', textAlign: 'center', padding: 12 }}>Loading UPS data...</div>}
                            {upsError && <div style={{ color: 'var(--red)' }}>Error: {upsError}</div>}
                            {upsData && (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                                  <strong style={{ color: 'var(--navy)', fontSize: 13 }}>{upsData.trackingNumber}</strong>
                                  <span style={{ fontWeight: 700, color: upsData.statusCode === '011' ? 'var(--green)' : 'var(--orange)' }}>{upsData.status}</span>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', marginBottom: 10 }}>
                                  {upsData.service && <><span style={{ color: 'var(--gray-500)' }}>Service</span><span>{upsData.service}</span></>}
                                  {upsData.weight && <><span style={{ color: 'var(--gray-500)' }}>Weight</span><span>{upsData.weight}</span></>}
                                  {upsData.charges?.billedWeight && <><span style={{ color: 'var(--gray-500)' }}>Billed Wt</span><span>{upsData.charges.billedWeight}</span></>}
                                  {upsData.scheduledDelivery && <><span style={{ color: 'var(--gray-500)' }}>Scheduled</span><span>{fmtUpsDate(upsData.scheduledDelivery)}</span></>}
                                  {upsData.actualDelivery && <><span style={{ color: 'var(--gray-500)' }}>Delivered</span><span style={{ color: 'var(--green)', fontWeight: 600 }}>{fmtUpsDate(upsData.actualDelivery)}</span></>}
                                </div>
                                {upsData.referenceNumbers && upsData.referenceNumbers.length > 0 && (
                                  <div style={{ marginBottom: 10, borderTop: '1px solid var(--gray-100)', paddingTop: 8 }}>
                                    <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 4, fontSize: 11 }}>Reference Numbers</div>
                                    {upsData.referenceNumbers.map((r, i) => (
                                      <div key={i} style={{ display: 'flex', gap: 8, fontSize: 11, marginBottom: 2 }}>
                                        {r.type && <span style={{ color: 'var(--gray-500)', minWidth: 40 }}>{r.type}</span>}
                                        <span style={{ fontWeight: 600 }}>{r.value}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {upsData.deliveryGps && (() => {
                                  const g = upsData.deliveryGps;
                                  const far = g.feet != null && g.feet > 250;
                                  const mid = g.feet != null && g.feet > 100 && !far;
                                  const tone = far ? 'var(--red)' : mid ? 'var(--orange)' : 'var(--green)';
                                  return (
                                    <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 8, marginBottom: 8 }}>
                                      <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>Where it was delivered</div>
                                      {g.feet != null ? (
                                        <div style={{ fontWeight: 700, color: tone, fontSize: 13 }}>
                                          Photo taken {g.feet.toLocaleString()} ft from the address
                                        </div>
                                      ) : (
                                        <div style={{ color: 'var(--gray-600)' }}>UPS gave the photo location but not the distance.</div>
                                      )}
                                      <div style={{ color: 'var(--gray-500)', fontSize: 11, margin: '2px 0 5px' }}>
                                        {far ? 'That is a long way off. Check the map: it may be the wrong house.'
                                          : mid ? 'Further than a normal porch. Worth a look at the map.'
                                          : g.feet != null ? 'That is normal for a front door.' : ''}
                                      </div>
                                      <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
                                        {g.bothMapUrl && <a href={g.bothMapUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--orange)', fontWeight: 600, textDecoration: 'underline' }}>Map: address to photo spot</a>}
                                        <a href={g.photoMapUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--orange)', fontWeight: 600, textDecoration: 'underline' }}>Map: photo spot only</a>
                                      </div>
                                      <div style={{ color: 'var(--gray-400)', fontSize: 10, marginTop: 4 }}>GPS {g.photoLat}, {g.photoLng}</div>
                                    </div>
                                  );
                                })()}
                                {(upsData.podImage || upsData.podCaptured || upsData.podLetterHtml) && (
                                  <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 8, marginBottom: 8 }}>
                                    <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>Proof of Delivery</div>
                                    {upsData.podImage && (
                                      <img src={upsData.podImage} alt="Delivery photo" style={{ width: '100%', borderRadius: 6, border: '1px solid var(--gray-200)', marginBottom: 6 }} />
                                    )}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11 }}>
                                      {upsData.podLetterHtml && (
                                        <a
                                          onClick={() => { const w = window.open('', '_blank'); if (w) { w.document.write(upsData.podLetterHtml); w.document.close(); } }}
                                          style={{ color: 'var(--orange)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                                        >
                                          <Package size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                          View UPS Proof of Delivery letter
                                        </a>
                                      )}
                                      {!upsData.podImage && upsData.podCaptured && (
                                        <>
                                          <a
                                            href={upsData.podUrl || `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(upsData.trackingNumber || trackingNumber)}&requester=ST/trackdetails`}
                                            target="_blank" rel="noopener noreferrer"
                                            style={{ color: 'var(--orange)', fontWeight: 600, textDecoration: 'underline' }}
                                          >
                                            <Package size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                            View delivery photo on ups.com
                                          </a>
                                          <div style={{ color: 'var(--gray-400)', fontStyle: 'italic', marginTop: 3 }}>
                                            Photo captured (disposition {upsData.podDisposition || '—'}) but not delivered through our API — needs UPS "Track Alert with Photo" linked to our API Client ID.
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {upsData.activities && upsData.activities.length > 0 && (
                                  <>
                                    <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 6, borderTop: '1px solid var(--gray-100)', paddingTop: 8 }}>Activity</div>
                                    {upsData.activities.map((a, i) => (
                                      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4, alignItems: 'flex-start' }}>
                                        <span style={{ color: 'var(--gray-400)', whiteSpace: 'nowrap', minWidth: 120 }}>{fmtUpsDate(a.date, a.time)}</span>
                                        <span style={{ color: i === 0 ? 'var(--navy)' : 'var(--gray-600)', fontWeight: i === 0 ? 600 : 400 }}>
                                          {a.description}{a.location ? ` · ${a.location}` : ''}
                                        </span>
                                      </div>
                                    ))}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="collapsible-header" onClick={() => setShowOrderNotes(!showOrderNotes)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>Order Notes (Internal)</span>
                {internalNotes.length > 0 && <span style={{ background: 'var(--orange)', color: 'white', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{internalNotes.length}</span>}
              </div>
              <ChevronDown size={15} style={{ transform: showOrderNotes ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>
            {showOrderNotes && (
              <div style={{ padding: 16 }}>
                {internalNotes.length > 0 && (
                  <div style={{ marginBottom: 12, background: 'var(--gray-50)', borderRadius: 4 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--gray-200)' }}>
                          <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--gray-500)', fontWeight: 600 }}>Date</th>
                          <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--gray-500)', fontWeight: 600 }}>Description</th>
                          <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--gray-500)', fontWeight: 600 }}>Response</th>
                          <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--gray-500)', fontWeight: 600 }}>Employee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {internalNotes.map((n, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                            <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>{fmtNoteDate(n.date)}</td>
                            <td style={{ padding: '4px 8px' }}>{n.description}</td>
                            <td style={{ padding: '4px 8px' }}>{n.response}</td>
                            <td style={{ padding: '4px 8px' }}>{n.employee}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <textarea
                  className="form-textarea"
                  placeholder="Add internal note..."
                  style={{ minHeight: 80 }}
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                />
                <button className="btn btn-primary" style={{ marginTop: 8 }} disabled={noteLoading || !newNote.trim()} onClick={() => handleAddNote(1)}>
                  {noteLoading ? 'Saving...' : 'Add Note'}
                </button>
              </div>
            )}

            <div className="collapsible-header" onClick={() => setShowSystemNotes(!showSystemNotes)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>System Notes</span>
                {systemNotes.length > 0 && <span style={{ background: 'var(--orange)', color: 'white', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{systemNotes.length}</span>}
              </div>
              <ChevronDown size={15} style={{ transform: showSystemNotes ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>
            {showSystemNotes && (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-700)', background: 'var(--gray-50)' }}>
                {systemNotes.length === 0 ? (
                  <p>No system notes.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--gray-200)' }}>
                        <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--gray-500)', fontWeight: 600 }}>Date</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--gray-500)', fontWeight: 600 }}>Description</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--gray-500)', fontWeight: 600 }}>Response</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--gray-500)', fontWeight: 600 }}>Employee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {systemNotes.map((n, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                          <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>{fmtNoteDate(n.date)}</td>
                          <td style={{ padding: '4px 8px' }}>{n.description}</td>
                          <td style={{ padding: '4px 8px' }}>{n.response}</td>
                          <td style={{ padding: '4px 8px' }}>{n.employee}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            <div className="collapsible-header" onClick={() => setShowProblems(!showProblems)}>
              <span>Problems</span>
              <ChevronDown size={15} style={{ transform: showProblems ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>
            {showProblems && (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No problems recorded.</div>
            )}

            <div style={{ padding: '12px 16px', background: 'var(--gray-50)', borderTop: '1px solid var(--gray-200)', display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12 }}>
              <span><strong>Crab Steamed Date:</strong> {order.crabSteamedDate ? <span>{new Date(order.crabSteamedDate).toLocaleDateString('en-US')}</span> : <span className="text-orange">Unknown</span>}</span>
              <span><strong>Packer:</strong> {order.packerName ? order.packerName.toUpperCase() : '—'}</span>
              <span><strong>Box Type:</strong> {order.boxType || '—'}</span>
              <span><strong>Gel Packs:</strong> {order.gelPacks || '—'}</span>
              <span><strong>Dry Ice:</strong> {order.dryIce || '—'}</span>
              <span><strong>Crab Count:</strong> {order.crabCount || 0}</span>
              <span><strong>Box Weight:</strong> {order.boxWeight > 0 ? `${order.boxWeight} lbs` : upsWeight ? `${upsWeight} (UPS)` : '—'}</span>
            </div>

            <div className="od-items-trans">
            <div className="od-table-wrap" style={{ padding: 16 }}>
              <table className="product-table" style={vipHeaderBg ? { '--vip-header-bg': vipHeaderBg, '--vip-header-color': vipHeaderColor } : undefined}>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Total</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {order.products.map(p => (
                    <tr key={p.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {p.image
                            ? <img src={p.image} alt={p.name} style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6, display: 'block' }} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                            : null}
                          <div style={{ width: 50, height: 50, borderRadius: 6, background: 'var(--gray-100)', display: p.image ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🦀</div>
                          <div>
                            <div className="product-name">{p.name?.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()}</div>
                          </div>
                        </div>
                      </td>
                      <td>{p.qty}</td>
                      <td>{p.price}</td>
                      <td>{p.total}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-outline" style={{ padding: '4px 8px' }}><Edit2 size={12} /></button>
                          <button className="btn btn-outline" style={{ padding: '4px 8px', color: 'var(--red)' }}><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {order.lineItems.map((item, i) => (
                    <tr key={i} className="line-item-row">
                      <td colSpan={2} className="line-item-label">
                        {item.isLink ? <a className="text-orange" style={{ cursor: 'pointer' }}>{item.label}</a> : item.label}
                      </td>
                      <td colSpan={2} style={{ textAlign: 'right', color: item.price.startsWith('-') ? 'var(--red)' : 'inherit', fontWeight: 600 }}>{item.price}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-outline" style={{ padding: '3px 6px' }}><Edit2 size={11} /></button>
                          <button className="btn btn-outline" style={{ padding: '3px 6px', color: 'var(--red)' }}><Trash2 size={11} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="order-total-row">Total: {order.total}</div>

              <div className="action-buttons">
                <button className="btn btn-outline">All Pricing to Zero</button>
                <button className="btn btn-outline">Recalculate Order Total</button>
                <button className="btn btn-outline">Add Surcharge</button>
                <button className="btn btn-primary">Add A Product</button>
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--gray-200)', padding: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', marginBottom: 12 }}>Transactions</div>
              <table className="trans-table" style={vipHeaderBg ? { '--vip-header-bg': vipHeaderBg, '--vip-header-color': vipHeaderColor } : undefined}>
                <thead>
                  <tr>
                    <th>Trans ID</th>
                    <th>Card</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>AVS</th>
                    <th>CVV2</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(payments) && payments.length > 0 ? payments.map((t, i) => (
                  <tr key={i}>
                    <td>
                      <div>{String(t.transactionId || '').split('#')[0]}</div>
                      <div style={{ color: 'var(--gray-500)', fontSize: 10 }}>{t.date ? new Date(t.date).toLocaleString() : ''}</div>
                    </td>
                    <td>{t.cardNo}</td>
                    <td>{t.cardType}</td>
                    <td><span style={{ color: t.paymentType === 'Declined' || t.paymentType === 'Error' ? 'var(--red)' : 'var(--green)', fontWeight: 700 }}>{t.paymentType}</span></td>
                    <td><strong>${Number(t.amount || 0).toFixed(2)}</strong></td>
                    <td style={{ fontSize: 10.5 }}>{t.avsCode}</td>
                    <td>{t.cvvCodeStatus}</td>
                    <td />
                  </tr>
                  )) : (
                  <tr><td colSpan={8} style={{ padding: '8px 0', fontSize: 11, color: 'var(--gray-500)' }}>{payments === null ? 'Loading transactions...' : payments === 'error' ? 'Could not load transactions. Do not charge from this page until they show.' : 'No transaction data.'}</td></tr>
                  )}
                </tbody>
              </table>

              <div style={{ marginTop: 12, fontSize: 12, textAlign: 'right', lineHeight: 2 }}>
                {order.giftCertificate && <div>Gift Certificate (<a className="text-orange" style={{ cursor: 'pointer' }}>{order.giftCertificate.code}</a>): <strong>{order.giftCertificate.amount}</strong></div>}
                <div>Coupon Discount: <strong>{order.couponDiscount}</strong></div>
                <div>Balance: <strong style={{ color: 'var(--green)' }}>{order.balance}</strong></div>
              </div>

              <div style={{ marginTop: 12, textAlign: 'right' }}>
                <button className="btn btn-primary">Manual Entry</button>
              </div>
              <CardActions orderId={order.internalId} customerId={order.customer.id} balance={order.balance} payments={Array.isArray(payments) ? payments : []}
                onChanged={() => fetchOrderPayments(order.internalId).then(rows => setPayments(Array.isArray(rows) ? rows : [])).catch(() => setPayments('error'))} />
            </div>
            </div>

            <div style={{ padding: 16, borderTop: '1px solid var(--gray-200)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="form-label">Packing Notes (Admin)</label>
                  <textarea className="form-textarea" value={packingNotes} onChange={e => setPackingNotes(e.target.value)} />
                </div>
                <div>
                  <label className="form-label">Gift Message</label>
                  <textarea className="form-textarea" value={giftMessage} onChange={e => setGiftMessage(e.target.value)} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button className="btn btn-primary" style={{ flex: 1 }} disabled={saving} onClick={handleSave}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                {saveMsg && (
                  <span style={{ fontSize: 12, fontWeight: 600, color: saveMsg.ok ? 'var(--green)' : 'var(--red)' }}>
                    {saveMsg.text}
                  </span>
                )}
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: 16 }}>
                <a className="text-orange" style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600 }} onClick={() => setShowPackingInvoice(true)}>Packing Invoice</a>
                <a className="text-orange" style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Shipping Invoice</a>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: CUSTOMER */}
        <div className="customer-panel">
          <div className="card">

            <div className="collapsible-header" onClick={() => setShowAdditional(!showAdditional)}>
              <span>Additional Order Information</span>
              <ChevronDown size={15} style={{ transform: showAdditional ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>
            {showAdditional && order && (
              <div style={{ padding: '12px 16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', fontSize: 12 }}>
                  {order.takenBy && (
                    <>
                      <div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Order Taken By</div>
                      <div>{order.takenBy}</div>
                    </>
                  )}
                  <div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Declared Value</div>
                  <div>${order.declaredValue?.toFixed(0) ?? '0'}</div>
                  <div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Insurance Cost</div>
                  <div>${order.insuranceCost?.toFixed(2) ?? '0.00'}</div>
                  <div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Order Profit</div>
                  <div style={{ color: order.orderProfit < 0 ? '#c00' : 'var(--gray-700)', fontWeight: 600 }}>
                    {order.orderProfit < 0 ? '-' : ''}${Math.abs(order.orderProfit ?? 0).toFixed(2)}
                  </div>
                  {order.boxWeight > 0 && (
                    <>
                      <div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Box Weight</div>
                      <div>{order.boxWeight} lbs</div>
                    </>
                  )}
                  {order.scaleShipCost > 0 && (
                    <>
                      <div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Scale Ship Cost</div>
                      <div>${order.scaleShipCost.toFixed(2)}</div>
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12, fontSize: 11 }}>
                  {[
                    { label: 'Is Fraud', active: order.isFraud },
                    { label: 'Crab Event', active: order.crabEvent },
                    { label: 'Claim Order', active: order.claimOrder },
                    { label: 'Gift Card(s) Sent', active: order.physicalGiftCardSent },
                    { label: 'Product Alert', active: order.productAlert },
                  ].map(({ label, active }) => (
                    <span key={label} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 8px', borderRadius: 4, fontWeight: 600,
                      background: active ? 'var(--orange)' : 'var(--gray-100)',
                      color: active ? '#fff' : 'var(--gray-400)',
                      border: `1px solid ${active ? 'var(--orange)' : 'var(--gray-200)'}`,
                    }}>
                      {active ? '\u2713' : ''} {label}
                    </span>
                  ))}
                  {(() => {
                    const hasRec = packingStatus && packingStatus.status === 'ready';
                    return (
                      <span
                        onClick={() => { if (hasRec) setShowPackingVideo(true); }}
                        title={hasRec ? 'View the recorded packing footage' : 'Record packing from the Packing Invoice first'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 8px', borderRadius: 4, fontWeight: 600,
                          cursor: hasRec ? 'pointer' : 'default',
                          background: hasRec ? 'var(--orange)' : 'var(--gray-100)',
                          color: hasRec ? '#fff' : 'var(--gray-400)',
                          border: `1px solid ${hasRec ? 'var(--orange)' : 'var(--gray-200)'}`,
                        }}
                      >
                        {hasRec ? '\u25b6' : ''} View Packing
                      </span>
                    );
                  })()}
                </div>
                {order.claimNumber && (
                  <div style={{ marginTop: 8, fontSize: 12 }}>
                    <span style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Claim Number: </span>
                    {order.claimNumber}
                  </div>
                )}
                {socialNotes.length > 0 && (
                  <div style={{ marginTop: 12, padding: '10px 12px', background: '#fffaf0', border: '1px solid #f0c36d', borderRadius: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#7a5b00', marginBottom: 6 }}>
                      ⚠ This customer has {socialNotes.length} social media note{socialNotes.length === 1 ? '' : 's'} — read before handling
                    </div>
                    {socialNotes.slice(0, 3).map(n => (
                      <div key={n.id} style={{ fontSize: 11.5, color: 'var(--navy)', padding: '3px 0', lineHeight: 1.45 }}>
                        <span style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', color: '#fff', background: n.platform === 'instagram' ? '#E1306C' : '#1877F2', borderRadius: 3, padding: '1px 5px', marginRight: 6 }}>{n.platform === 'instagram' ? 'IG' : 'FB'}</span>
                        “{String(n.comment_text).slice(0, 180)}{String(n.comment_text).length > 180 ? '…' : ''}”
                        {n.permalink && <a href={n.permalink} target="_blank" rel="noreferrer" style={{ marginLeft: 6, color: 'var(--orange)', fontWeight: 600 }}>view ↗</a>}
                      </div>
                    ))}
                    {socialNotes.length > 3 && <div style={{ fontSize: 10.5, color: '#9a7b20', marginTop: 3 }}>+{socialNotes.length - 3} more on the customer record (Customers &gt; Search)</div>}
                  </div>
                )}
              </div>
            )}

            <div className="collapsible-header" onClick={() => setShowHistory(!showHistory)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>History</span>
                <span style={{ background: 'var(--navy)', color: 'white', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{orderHistory?.summary?.totalNumberOfOrders ?? 0}</span>
              </div>
              <ChevronDown size={15} style={{ transform: showHistory ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>
            {showHistory && (
              <div style={{ padding: 16 }}>
                {orderHistory?.orders?.length > 0 && (
                  <div style={{ maxHeight: 320, overflow: 'auto' }}>
                    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--gray-200)' }}>
                          <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--gray-500)', fontWeight: 600 }}>Order</th>
                          <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--gray-500)', fontWeight: 600 }}>Date</th>
                          <th style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--gray-500)', fontWeight: 600 }}>Amount</th>
                          <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--gray-500)', fontWeight: 600 }}>Ship Method</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderHistory.orders.map(o => (
                          <tr key={o.internalId || o.id} style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                            onClick={() => navigateTo('order-details', String(o.id))}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                            onMouseLeave={e => e.currentTarget.style.background = ''}>
                            <td style={{ padding: '5px 8px', fontWeight: 600, color: 'var(--navy)' }}>#{o.id}</td>
                            <td style={{ padding: '5px 8px' }}>{o.date ? new Date(o.date).toLocaleDateString('en-US') : '—'}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>${o.amount.toFixed(2)}</td>
                            <td style={{ padding: '5px 8px', fontSize: 11 }}>{o.shipMethod || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            <div className="collapsible-header od-tech-header" onClick={() => setShowTech(!showTech)}>
              <span>Tech Info</span>
              <ChevronDown size={15} style={{ transform: showTech ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>
            {showTech && (
              <div style={{ padding: '12px 16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', fontSize: 12 }}>
                  {order.orderReferredBy && (
                    <>
                      <div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Order Referred By</div>
                      <div>{order.orderReferredBy}</div>
                    </>
                  )}
                  <div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>IP Rating</div>
                  <div>{order.ipRating ?? 0} ({order.ipRatingGrade})</div>
                </div>
                {attribution && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--gray-200)' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>Attribution / Traffic Source</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px', fontSize: 12 }}>
                      {attribution.utm_source && (<><div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Source</div><div>{attribution.utm_source}</div></>)}
                      {attribution.utm_medium && (<><div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Medium</div><div>{attribution.utm_medium}</div></>)}
                      {attribution.utm_campaign && (<><div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Campaign</div><div>{attribution.utm_campaign}</div></>)}
                      {attribution.utm_content && (<><div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Content</div><div>{attribution.utm_content}</div></>)}
                      {attribution.utm_term && (<><div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Term</div><div>{attribution.utm_term}</div></>)}
                      {attribution.fbclid && (<><div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Facebook Click</div><div style={{ color: 'var(--green, #1a8a4a)', fontWeight: 600 }}>Yes</div></>)}
                      {attribution.gclid && (<><div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Google Click</div><div style={{ color: 'var(--green, #1a8a4a)', fontWeight: 600 }}>Yes</div></>)}
                      {attribution.landing_page && attribution.landing_page !== '/' && (<><div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Landing Page</div><div style={{ wordBreak: 'break-all' }}>{attribution.landing_page}</div></>)}
                      {attribution.referrer && (<><div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Referrer</div><div style={{ wordBreak: 'break-all' }}>{attribution.referrer}</div></>)}
                    </div>
                  </div>
                )}
                {!attribution && <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 8 }}>No traffic source recorded for this order.</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Address Picker Modal */}
      {addressPickerType && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setAddressPickerType(null)}>
          <div style={{ background: '#fff', borderRadius: 10, width: 560, maxHeight: '80vh', overflow: 'auto', padding: 24 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15, color: 'var(--navy)' }}>
                Select {addressPickerType === 'billing' ? 'Billing' : 'Shipping'} Address
              </h3>
              <X size={18} style={{ cursor: 'pointer', color: 'var(--gray-500)' }} onClick={() => setAddressPickerType(null)} />
            </div>
            {bcAddressesLoading && <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: 20, textAlign: 'center' }}>Loading addresses from BigCommerce...</div>}
            {!bcAddressesLoading && bcAddresses.length === 0 && !newAddr && (
              <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: 20, textAlign: 'center' }}>No addresses found in BigCommerce for this customer.</div>
            )}
            {!bcAddressesLoading && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12 }}>
                {bcAddresses.map((a, i) => (
                  <div key={a.id || i}
                    onClick={() => selectAddress(a)}
                    style={{
                      padding: '12px 14px', border: '1px solid var(--gray-200)', borderRadius: 6,
                      background: i === 0 ? '#f0f7ff' : 'var(--gray-50)', fontSize: 12, cursor: 'pointer',
                      transition: 'border-color .15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--orange)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--gray-200)'}>
                    <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{toTitleCase(a.first_name)} {toTitleCase(a.last_name)}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {i === 0 && <span style={{ fontSize: 9, fontWeight: 600, background: 'var(--navy)', color: '#fff', padding: '1px 6px', borderRadius: 4 }}>DEFAULT</span>}
                        <span onClick={e => deleteAddress(a, e)} title="Delete address"
                          style={{ color: 'var(--gray-400)', cursor: 'pointer', display: 'flex' }}
                          onMouseEnter={e => e.currentTarget.style.color = '#c0392b'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--gray-400)'}><Trash2 size={12} /></span>
                      </span>
                    </div>
                    {a.company && <div style={{ color: 'var(--gray-700)', marginBottom: 2 }}>{toTitleCase(a.company)}</div>}
                    <div style={{ color: 'var(--gray-700)' }}>{toTitleCase(a.address1 || a.street_1 || '')}</div>
                    {(a.address2 || a.street_2) && <div style={{ color: 'var(--gray-700)' }}>{toTitleCase(a.address2 || a.street_2)}</div>}
                    <div style={{ color: 'var(--gray-700)' }}>
                      {toTitleCase(a.city || '')}, {stateAbbr(a.state_or_province || a.state || '')} {a.postal_code || a.zip || ''}
                    </div>
                    {a.phone && <div style={{ color: 'var(--gray-500)', marginTop: 4, fontSize: 11 }}>{fmtPhone(a.phone)}</div>}
                    {a.address_type && <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 2, textTransform: 'capitalize' }}>{a.address_type}</div>}
                  </div>
                ))}
                {/* + New Address card */}
                {!newAddr && bcCustomerId && (
                  <div onClick={() => setNewAddr({ ...emptyAddr })}
                    style={{
                      padding: '12px 14px', border: '2px dashed var(--gray-300)', borderRadius: 6,
                      background: 'var(--gray-50)', fontSize: 12, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 80,
                      color: 'var(--gray-500)', fontWeight: 600, transition: 'border-color .15s, color .15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--orange)'; e.currentTarget.style.color = 'var(--orange)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--gray-300)'; e.currentTarget.style.color = 'var(--gray-500)'; }}>
                    + New Address
                  </div>
                )}
              </div>
            )}
            {/* New Address Form */}
            {newAddr && (
              <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--gray-200)', borderRadius: 8, background: 'var(--gray-50)' }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', marginBottom: 12 }}>New Address</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px', fontSize: 12 }}>
                  <input className="form-input" placeholder="First Name *" value={newAddr.first_name} onChange={e => setNewAddr(a => ({ ...a, first_name: e.target.value }))} style={{ fontSize: 12 }} />
                  <input className="form-input" placeholder="Last Name" value={newAddr.last_name} onChange={e => setNewAddr(a => ({ ...a, last_name: e.target.value }))} style={{ fontSize: 12 }} />
                  <input className="form-input" placeholder="Company" value={newAddr.company} onChange={e => setNewAddr(a => ({ ...a, company: e.target.value }))} style={{ fontSize: 12, gridColumn: '1 / -1' }} />
                  <input className="form-input" placeholder="Address Line 1 *" value={newAddr.address1} onChange={e => setNewAddr(a => ({ ...a, address1: e.target.value }))} style={{ fontSize: 12, gridColumn: '1 / -1' }} />
                  <input className="form-input" placeholder="Address Line 2" value={newAddr.address2} onChange={e => setNewAddr(a => ({ ...a, address2: e.target.value }))} style={{ fontSize: 12, gridColumn: '1 / -1' }} />
                  <input className="form-input" placeholder="City *" value={newAddr.city} onChange={e => setNewAddr(a => ({ ...a, city: e.target.value }))} style={{ fontSize: 12 }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select className="form-input" value={newAddr.state_or_province} onChange={e => setNewAddr(a => ({ ...a, state_or_province: e.target.value }))} style={{ fontSize: 12, width: '50%' }}>
                      <option value="">State *</option>
                      {US_STATES.map(([abbr, name]) => <option key={abbr} value={name}>{abbr}</option>)}
                    </select>
                    <input className="form-input" placeholder="ZIP *" value={newAddr.postal_code} onChange={e => setNewAddr(a => ({ ...a, postal_code: e.target.value }))} style={{ fontSize: 12, width: '50%' }} />
                  </div>
                  <input className="form-input" placeholder="Phone" value={newAddr.phone} onChange={e => setNewAddr(a => ({ ...a, phone: e.target.value }))} style={{ fontSize: 12 }} />
                  <select className="form-input" value={newAddr.address_type} onChange={e => setNewAddr(a => ({ ...a, address_type: e.target.value }))} style={{ fontSize: 12 }}>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                  <button className="btn" onClick={() => setNewAddr(null)} style={{ fontSize: 12, padding: '6px 14px' }}>Cancel</button>
                  <button className="btn btn-primary" onClick={saveNewAddress} disabled={newAddrSaving} style={{ fontSize: 12, padding: '6px 14px' }}>
                    {newAddrSaving ? 'Saving...' : 'Save & Select'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* File Claim Modal */}
      {showClaimModal && order && (
        <FileClaimModal order={order} onClose={() => setShowClaimModal(false)} navigateTo={navigateTo} />
      )}
      {showPackingInvoice && order && (
        <PackingInvoiceModal
          order={order}
          shippingInstructions={shippingInstructions}
          initialStatus={packingStatus}
          onClose={() => setShowPackingInvoice(false)}
          onReady={(row) => setPackingStatus(row)}
        />
      )}
      {showPackingVideo && order && (
        <PackingVideoOverlay order={order} onClose={() => setShowPackingVideo(false)} />
      )}
      {/* VIP Cash Rewards history modal */}
      <VipRewardsModal
        open={rewardsOpen}
        onClose={() => setRewardsOpen(false)}
        bcCustomerId={bcCustomerId}
        customerName={order?.customer ? toTitleCase(order.customer.billing?.name || '') : ''}
        storeCredit={storeCredit}
        showDrift={true}
      />
    </div>
  );
}

// --- Inline File Claim Modal (lives on Order Details page) ---
function FileClaimModal({ order, onClose, navigateTo }) {
  const parseDateStr = (s) => {
    if (!s) return null;
    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (m) {
      let yr = parseInt(m[3], 10);
      if (yr < 100) yr += 2000;
      let timeStr = s.slice(m[0].length).trim().replace(/(\d)(AM|PM)/i, '$1 $2');
      if (timeStr) { const d = new Date(`${m[1]}/${m[2]}/${yr} ${timeStr}`); if (!isNaN(d)) return d; }
      return new Date(yr, parseInt(m[1],10) - 1, parseInt(m[2],10));
    }
    const d = new Date(s); return isNaN(d) ? null : d;
  };
  const toISO = (s) => { const d = parseDateStr(s); return d ? d.toISOString().slice(0,10) : new Date().toISOString().slice(0,10); };

  const selectedDate = parseDateStr(order.selectedDelivery);
  const actualDate = parseDateStr(order.actualDelivery);
  const isLate = selectedDate && actualDate && actualDate > selectedDate;

  // Compute merchandise total from products
  const productTotal = order.products?.reduce((sum, p) => {
    const price = parseFloat(String(p.price || '0').replace(/[$,]/g, ''));
    const qty = Number(p.qty || 1);
    const total = parseFloat(String(p.total || '0').replace(/[$,]/g, ''));
    return sum + (total > 0 ? total : price * qty);
  }, 0) || 0;
  // Shipping cost from lineItems
  const shipLine = order.lineItems?.find(li => li.label && !li.label.includes('Packing') && !li.label.includes('Adjustment') && !li.label.includes('Saturday') && !li.label.includes('Surcharge') && !li.label.includes('Coupon') && !li.label.includes('Gift'));
  const shipCostVal = shipLine ? parseFloat(shipLine.price?.replace(/[$,]/g, '') || '0') : 0;
  const orderTotal = parseFloat(String(order.total || '0').replace(/[$,]/g, '')) || 0;
  const merchAmount = productTotal > 0 ? productTotal : (orderTotal > 0 ? orderTotal : 0);

  const [lossCause, setLossCause] = useState(isLate ? 'delay' : 'damage_Ext');
  const [lossDate, setLossDate] = useState(order.actualDelivery ? toISO(order.actualDelivery) : new Date().toISOString().slice(0,10));
  const [merchandiseAmt, setMerchandiseAmt] = useState(String(merchAmount.toFixed(2)));
  const [shippingAmt, setShippingAmt] = useState(String(shipCostVal.toFixed(2)));
  const [damageDesc, setDamageDesc] = useState(isLate ? 'Box was delivered late. Contents spoiled.' : '');
  const [merchDesc, setMerchDesc] = useState('Perishable seafood shipment');
  const [notes, setNotes] = useState(isLate ? `Late delivery: selected ${order.selectedDelivery}, actual ${order.actualDelivery}` : '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const LOSS_CAUSES = [
    { value: 'damage_Ext', label: 'Damaged in transit' },
    { value: 'loss_Ext', label: 'Lost package' },
    { value: 'fallout_Ext', label: 'Missing contents' },
    { value: 'delay', label: 'Late delivery / Delay' },
    { value: 'abandonment', label: 'Abandonment' },
  ];

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      const shipDate = order.shipDate ? toISO(order.shipDate) : toISO(order.orderDate);
      const customerName = (order.customer?.shipping?.name || order.customer?.billing?.name || '').trim();

      const ship = order.customer?.shipping || {};
      const rawRes = await fetch('/.netlify/functions/insureshield-proxy', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create-claim',
          trackingNumber: order.trackingNumber || 'UNKNOWN',
          shipDate, lossDate, lossCause,
          merchandiseAmount: parseFloat(merchandiseAmt) || 0,
          shippingAmount: parseFloat(shippingAmt) || 0,
          merchandiseDescription: merchDesc,
          damageDescription: (lossCause === 'damage_Ext' || lossCause === 'delay') ? damageDesc : undefined,
          additionalNotes: notes,
          commodity: 'perishables_fruits', carrier: 'UPS',
          recipientName: customerName,
          recipientAddress: ship.address || '',
          recipientCity: ship.city || '',
          recipientState: ship.state || '',
          recipientPostalCode: ship.zip || '',
          contactEmail: order.customer?.billing?.email || 'claims@crabplace.com',
        }),
      });
      const resText = await rawRes.text();
      let apiRes;
      try { apiRes = JSON.parse(resText); } catch(e) {
        throw new Error(resText.includes('Postal Code') ? 'Postal Code is missing or invalid for recipientDetails.' : 'InsureShield API error (non-JSON response). Status: ' + rawRes.status);
      }

      if (apiRes?.errors?.length) throw new Error(apiRes.errors[0].errorMessage || 'InsureShield API error');
      if (apiRes?.error) throw new Error(apiRes.error);
      const claimNumber = apiRes?.claimNumber;
      if (!claimNumber) throw new Error('No claim number in response: ' + JSON.stringify(apiRes).substring(0, 300));

      // Save to Neon
      const claimAmount = (parseFloat(merchandiseAmt) || 0) + (parseFloat(shippingAmt) || 0);
      await fetch('/.netlify/functions/neon', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...sessionHeaders() },
        body: JSON.stringify({ query: 'claims_create', params: {
          claim_number: claimNumber,
          order_id: String(order.internalId || order.id),
          bc_order_id: String(order.id),
          tracking_number: order.trackingNumber || '',
          customer_name: customerName,
          customer_email: order.customer?.billing?.email || '',
          ship_date: shipDate, loss_date: lossDate, loss_cause: lossCause,
          merchandise_description: merchDesc,
          merchandise_amount: parseFloat(merchandiseAmt) || 0,
          shipping_amount: parseFloat(shippingAmt) || 0,
          claim_amount: claimAmount,
          status: 'Submitted', upsc_status: 'Claim Received',
          damage_description: damageDesc || null,
          additional_notes: notes || null,
          recipient_name: customerName,
        }}),
      });

      setResult({ claimNumber });
    } catch (err) { setError(err.message); }
    setSubmitting(false);
  }

  const [uploadingSlip, setUploadingSlip] = useState(false);
  const [slipUploaded, setSlipUploaded] = useState(false);
  const [slipError, setSlipError] = useState(null);

  async function handleUploadPackingSlip() {
    setUploadingSlip(true); setSlipError(null);
    try {
      const res = await fetch('/.netlify/functions/claim-packing-slip', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'upload', orderId: String(order.internalId || order.id), claimNumber: result.claimNumber }),
      }).then(r => r.json());
      if (res.error) throw new Error(res.error);
      setSlipUploaded(true);
    } catch (e) { setSlipError(e.message); }
    setUploadingSlip(false);
  }

  async function handleDownloadPackingSlip() {
    try {
      const res = await fetch('/.netlify/functions/claim-packing-slip', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'generate', orderId: String(order.internalId || order.id) }),
      }).then(r => r.json());
      if (res.error) throw new Error(res.error);
      // Download the PDF
      const link = document.createElement('a');
      link.href = 'data:application/pdf;base64,' + res.pdf;
      link.download = res.fileName;
      link.click();
    } catch (e) { alert('Failed to generate PDF: ' + e.message); }
  }

  if (result) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 32, textAlign: 'center', maxWidth: 480 }}>
          <div style={{ color: '#1e7e34', fontSize: 32, marginBottom: 8 }}>&#10003;</div>
          <h3 style={{ color: 'var(--navy)', margin: '0 0 8px' }}>Claim Filed Successfully</h3>
          <p style={{ fontSize: 14, color: 'var(--gray-500)' }}>Claim Number: <strong>{result.claimNumber}</strong></p>

          {/* Packing Slip Upload */}
          <div style={{ margin: '16px 0', padding: '12px 16px', background: '#f8f9fa', borderRadius: 8, textAlign: 'left' }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--navy)' }}>Attach Packing Slip</div>
            {slipUploaded ? (
              <div style={{ fontSize: 13, color: '#1e7e34', display: 'flex', alignItems: 'center', gap: 6 }}>
                &#10003; Packing slip uploaded to claim
              </div>
            ) : (
              <>
                <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '0 0 8px' }}>
                  Upload the packing slip PDF as supporting documentation for this claim.
                </p>
                {slipError && <div style={{ fontSize: 12, color: '#c62828', marginBottom: 6 }}>{slipError}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={handleUploadPackingSlip} disabled={uploadingSlip}>
                    {uploadingSlip ? 'Uploading...' : 'Upload to Claim'}
                  </button>
                  <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={handleDownloadPackingSlip}>
                    Download PDF
                  </button>
                </div>
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
            <button className="btn btn-outline" onClick={onClose}>Back to Order</button>
            <button className="btn btn-primary" onClick={() => navigateTo('ups-claims')}>View Claims</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: 'var(--navy)' }}>File InsureShield Claim</h3>
          <button className="btn btn-ghost" onClick={onClose} style={{ padding: 4 }}><X size={18} /></button>
        </div>

        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
          <strong>Order #{order.id}</strong> — {order.customer?.shipping?.name || order.customer?.billing?.name}
          {order.trackingNumber && <span style={{ marginLeft: 8, fontFamily: 'monospace', fontSize: 11 }}>{order.trackingNumber}</span>}
          {isLate && <span style={{ marginLeft: 8, color: 'var(--orange)', fontWeight: 600, fontSize: 11 }}>LATE DELIVERY</span>}
        </div>

        {error && (
          <div style={{ background: '#fde8e8', color: '#c62828', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Reason for Claim</label>
            <select className="form-select" value={lossCause} onChange={e => setLossCause(e.target.value)}>
              {LOSS_CAUSES.map(lc => <option key={lc.value} value={lc.value}>{lc.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div className="form-group">
              <label className="form-label">Loss Date</label>
              <input className="form-input" type="date" value={lossDate} onChange={e => setLossDate(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">Merchandise Amount ($)</label>
              <input className="form-input" type="number" step="0.01" value={merchandiseAmt} onChange={e => setMerchandiseAmt(e.target.value)} required />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div className="form-group">
              <label className="form-label">Shipping Amount ($)</label>
              <input className="form-input" type="number" step="0.01" value={shippingAmt} onChange={e => setShippingAmt(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Merchandise Description</label>
              <input className="form-input" value={merchDesc} onChange={e => setMerchDesc(e.target.value)} required />
            </div>
          </div>
          {(lossCause === 'damage_Ext' || lossCause === 'delay') && (
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Damage Description</label>
              <textarea className="form-textarea" rows={2} value={damageDesc} onChange={e => setDamageDesc(e.target.value)} />
            </div>
          )}
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label className="form-label">Additional Notes</label>
            <textarea className="form-textarea" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Claim to UPS Capital'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
